import React from 'react';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';

interface ManagedServicesProps {
  setCurrentPage: (page: string) => void;
}

const ManagedServices: React.FC<ManagedServicesProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: LucideIcons.Server,
      title: "Infrastructure Management",
      description: "Comprehensive management and monitoring of your IT infrastructure."
    },
    {
      icon: LucideIcons.Network,
      title: "Network Operations",
      description: "24/7 network monitoring, optimization, and maintenance."
    },
    {
      icon: LucideIcons.Cloud,
      title: "Cloud Services",
      description: "Expert management of cloud infrastructure and applications."
    },
    {
      icon: LucideIcons.Shield,
      title: "Security Management",
      description: "Robust security measures to protect your IT environment."
    }
  ];

  const services = [
    {
      title: "Infrastructure Services",
      items: [
        "Server management",
        "Network administration",
        "Storage solutions",
        "Backup and recovery"
      ]
    },
    {
      title: "Support Services",
      items: [
        "24/7 technical support",
        "Help desk services",
        "System maintenance",
        "Issue resolution"
      ]
    },
    {
      title: "Cloud Management",
      items: [
        "Cloud infrastructure",
        "Application hosting",
        "Resource optimization",
        "Performance monitoring"
      ]
    }
  ];

  const benefits = [
    {
      icon: LucideIcons.Clock,
      title: "24/7 Operations",
      description: "Round-the-clock monitoring and management of your IT infrastructure."
    },
    {
      icon: LucideIcons.Users,
      title: "Expert Support",
      description: "Access to certified IT professionals and specialists."
    },
    {
      icon: LucideIcons.LineChart,
      title: "Optimized Performance",
      description: "Continuous optimization and enhancement of IT systems."
    }
  ];

  const solutions = [
    {
      icon: LucideIcons.Monitor,
      title: "IT Operations",
      items: [
        "Infrastructure monitoring",
        "System administration",
        "Performance optimization",
        "Resource management"
      ]
    },
    {
      icon: LucideIcons.Database,
      title: "Data Management",
      items: [
        "Database administration",
        "Storage optimization",
        "Data backup",
        "Recovery services"
      ]
    },
    {
      icon: LucideIcons.Lock,
      title: "Security Operations",
      items: [
        "Security monitoring",
        "Threat prevention",
        "Access control",
        "Policy enforcement"
      ]
    },
    {
      icon: LucideIcons.Laptop,
      title: "End-User Support",
      items: [
        "Technical assistance",
        "Problem resolution",
        "System updates",
        "User training"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block mb-6"
            >
              <LucideIcons.Settings className="h-16 w-16 text-primary animate-glow" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              <span className="text-gradient">Managed IT Services</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-muted max-w-3xl mx-auto"
            >
              Comprehensive IT management solutions to optimize and secure your technology infrastructure
            </motion.p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Core Features</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive IT management features and capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="glass-panel rounded-xl p-6 hover-card"
                >
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-6">
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">IT Services</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive IT management services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <h3 className="text-2xl font-semibold mb-6">
                  {service.title}
                </h3>
                <ul className="space-y-4">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <LucideIcons.CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Key Benefits</span>
            </h2>
            <p className="text-lg text-muted">
              Advantages of our managed IT services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="glass-panel rounded-xl p-8 text-center hover-card"
                >
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-muted">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Solutions Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Comprehensive Solutions</span>
            </h2>
            <p className="text-lg text-muted">
              End-to-end IT management solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const IconComponent = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="glass-panel rounded-xl p-8 hover-card"
                >
                  <div className="flex items-center mb-6">
                    <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold ml-4">
                      {solution.title}
                    </h3>
                  </div>
                  <ul className="space-y-4">
                    {solution.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center">
                        <LucideIcons.CheckCircle className="h-5 w-5 text-primary mr-3" />
                        <span className="text-muted">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Ready to Optimize Your IT Infrastructure?
              </h2>
              <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
                Get started with our managed IT services today
              </p>
              <button 
                onClick={() => setCurrentPage('get-started')}
                className="btn-primary"
              >
                <span className="flex items-center justify-center">
                  Request Consultation
                  <LucideIcons.ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ManagedServices;